import { RefObject, useEffect, useState } from "react";
import { useStateContext } from "../../contexts/auth-context";
import { createGraphQLClientWithMiddleware } from "../../services/graphqlClient";
import {
  GenerateInventoryReportNoQueueQuery,
  InventoryReportType,
  ReportGenerateInput,
  useGenerateInventoryReportNoQueueQuery,
} from "../../generated/inventory";
import { AgGridReact } from "ag-grid-react";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

export const useInventoryReport = (
  gridRef: RefObject<AgGridReact<any>>,
  reportType: InventoryReportType
) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const {
    state: { authUser },
  } = useStateContext();

  const graphqlClient = createGraphQLClientWithMiddleware("wms");

  const [exportInput, setExportInput] = useState<ReportGenerateInput | null>(
    null
  );

  const {
    refetch: refetchData,
    isLoading: isExporting,
    isFetching,
  } = useGenerateInventoryReportNoQueueQuery<GenerateInventoryReportNoQueueQuery>(
    graphqlClient,
    {
      input: exportInput as ReportGenerateInput,
    },
    {
      enabled: false,
      retry: false,
    }
  );

  const onBtnExport = () => {
    try {
      const filterModel = gridRef?.current?.api.getFilterModel();
      const sortModel = gridRef?.current?.columnApi
        .getColumnState()
        .filter((s) => s.sort !== null)
        .map(({ sort, colId }) => ({
          sort,
          colId,
        }));
      setExportInput({
        report_type: reportType,
        priority: 1,
        user_unique_id: authUser?.unique_id || "",
        params: {
          aggridInput: {
            filterModel,
            sortModel,
          },
        },
      });
    } catch (err) {
      enqueueSnackbar("นำออกข้อมูลไม่สำเร็จ", {
        variant: "error",
      });
      setExportInput(null);
    }
  };

  useEffect(() => {
    const fetchDataAndOpenWindow = async () => {
      try {
        const { data: exportData } = await refetchData();
        if (exportData) {
          window.open(exportData?.GenerateReportNoQueue || "", "_blank");
          enqueueSnackbar(`${t("button.export")}สำเร็จ`, {
            variant: "success",
          });
        } else {
          enqueueSnackbar(`${t("button.export")}ไม่สำเร็จ`, {
            variant: "error",
          });
          setExportInput(null);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        enqueueSnackbar(`${t("button.export")}ไม่สำเร็จ`, {
          variant: "error",
        });
        setExportInput(null);
      }
    };

    if (exportInput !== null) {
      fetchDataAndOpenWindow();
    }
  }, [enqueueSnackbar, exportInput, refetchData, t]);

  return {
    onBtnExport,
    isExporting,
    isFetching,
  };
};
